import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AdSense from '../../components/AdSense';
import ProgressRing from './components/ProgressRing';
import MetricsCard from './components/MetricsCard';
import ActivityTimeline from './components/ActivityTimeline';
import PlanManagement from './components/PlanManagement';
import ExerciseSummary from './components/ExerciseSummary';
import CustomKPITracking from './components/CustomKPITracking';
import QuickActions from './components/QuickActions';
import SubscriptionStatus from './components/SubscriptionStatus';
import Footer from '../../components/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { loadAssessmentProgress } from '../../services/assessmentService';
import { loadCareerPlan } from '../../services/careerPlanService';

const ProgressDashboard = () => {
  const { user, userProfile, isPremium, loading: authLoading } = useAuth();
  // Unified dashboard audio file
  const audioSrc = '/assets/audio/Dashboard.mp3';
  const [loading, setLoading] = useState(true);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [activities, setActivities] = useState([]);
  const [upcomingExercises, setUpcomingExercises] = useState([]);
  const [overdueExercises, setOverdueExercises] = useState([]);
  const [customKPIs, setCustomKPIs] = useState([]);
  const [completionTrend, setCompletionTrend] = useState([]);

  // Update loading state based on auth
  useEffect(() => {
    setLoading(authLoading);
  }, [authLoading]);

  useEffect(() => {
    const initializeDashboard = async () => {
      if (!user) return;
      
      // Get authenticated user from AuthContext
      const currentUser = user;
      
      if (!currentUser) {
        // No user found - redirect to authentication
        window.location.href = '/authentication';
        return;
      }

      // Simulate small delay for UX
      await new Promise(resolve => setTimeout(resolve, 300));

      // Load real data from database
      try {
        // Load assessment data to check if user has completed assessment
        const assessmentResult = await loadAssessmentProgress(user.id);
        
        if (!assessmentResult.success || !assessmentResult.data || !assessmentResult.data.is_completed) {
          // Try to load any previously saved plan even if assessment service says incomplete
          const planResult = await loadCareerPlan(user.id);
          if (planResult.success && planResult.data) {
            setCurrentPlan({
              ...planResult.data,
              status: 'active'
            });
          } else {
            setCurrentPlan(null);
          }
          setActivities([]);
          setUpcomingExercises([]);
          setOverdueExercises([]);
          setCustomKPIs([]);
          setCompletionTrend([]);
          setLoading(false);
          return;
        }

        // User has completed assessment - prefer saved plan if exists
        const planResult = await loadCareerPlan(user.id);
        if (planResult.success && planResult.data) {
          setCurrentPlan({
            ...planResult.data,
            status: 'active'
          });
        } else {
          // Fallback to assessment-derived shell
          const assessmentData = assessmentResult.data;
          setCurrentPlan({
            title: `${assessmentData.assessment_setup?.occupation} Career Path`,
            progressPercentage: 0,
            completedExercises: 0,
            totalExercises: 0,
            completedGoals: 0,
            totalGoals: 0,
            estimatedWeeks: 0,
            estimatedHoursPerWeek: "3-5 hours"
          });
        }

        // Load real activities, exercises, and KPIs from database
        // For now, set empty arrays - these will be populated by real data
        setActivities([]);
        setUpcomingExercises([]);
        setOverdueExercises([]);
        setCustomKPIs([]);
        setCompletionTrend([]);

      } catch (error) {
        console.error('Error loading dashboard data:', error);
        // On error, show empty state
        setCurrentPlan(null);
        setActivities([]);
        setUpcomingExercises([]);
        setOverdueExercises([]);
        setCustomKPIs([]);
        setCompletionTrend([]);
      }
      setLoading(false);
    };

    initializeDashboard();

    // No localStorage listeners; data comes from database only
    return () => {};
  }, []);

  const handleRegeneratePlan = async () => {
    // Simulate plan regeneration
    console.log('Regenerating plan...');
    // In real app, this would call the AI plan generation API
  };

  const handleGenerateWeeklyCheckin = async () => {
    // Simulate weekly check-in generation
    console.log('Generating weekly check-in...');
    // In real app, this would generate a progress summary
  };

  const handleUpdateKPI = async (kpiId, newValue) => {
    setCustomKPIs(prev => prev?.map(kpi => 
      kpi?.id === kpiId 
        ? { ...kpi, currentValue: newValue, lastUpdated: new Date()?.toISOString() }
        : kpi
    ));
  };

  // Calculate overall metrics
  const overallProgress = currentPlan?.progressPercentage || 0;
  const completedExercises = currentPlan?.completedExercises || 0;
  const activeGoals = (currentPlan?.totalGoals || 0) - (currentPlan?.completedGoals || 0);
  // Compute current streak from latest activity metadata if available
  const currentStreak = (() => {
    if (!Array.isArray(activities) || activities.length === 0) return 0;
    const latest = activities.find(a => typeof a?.metadata?.streak === 'number');
    return latest ? Number(latest.metadata.streak) : 0;
  })();

  // Compute points and exercise trends (last 7 days vs prior 7 days)
  const { totalPoints, pointsTrend, pointsTrendDir, exercisesTrend, exercisesTrendDir } = (() => {
    const now = new Date();
    const end = now;
    const start = new Date(now);
    start.setDate(start.getDate() - 7);
    const prevEnd = new Date(start);
    const prevStart = new Date(start);
    prevStart.setDate(prevStart.getDate() - 7);

    const inRange = (d, s, e) => {
      const t = new Date(d);
      return t >= s && t < e;
    };

    const sumPoints = (arr) => arr.reduce((sum, a) => sum + (a?.metadata?.points || 0), 0);
    const isExercise = (a) => (a?.type === 'exercise_completed');

    const currActs = (activities || []).filter(a => a?.timestamp && inRange(a.timestamp, start, end));
    const prevActs = (activities || []).filter(a => a?.timestamp && inRange(a.timestamp, prevStart, prevEnd));

    const currPts = sumPoints(currActs);
    const prevPts = sumPoints(prevActs);
    const ptsTrend = prevPts === 0 ? (currPts > 0 ? 100 : 0) : Math.round(((currPts - prevPts) / prevPts) * 100);
    const ptsDir = currPts >= prevPts ? 'up' : 'down';

    const currEx = currActs.filter(isExercise).length;
    const prevEx = prevActs.filter(isExercise).length;
    const exTrend = prevEx === 0 ? (currEx > 0 ? 100 : 0) : Math.round(((currEx - prevEx) / prevEx) * 100);
    const exDir = currEx >= prevEx ? 'up' : 'down';

    const totalAllTime = (activities || []).reduce((sum, a) => sum + (a?.metadata?.points || 0), 0);
    return { totalPoints: totalAllTime, pointsTrend: ptsTrend, pointsTrendDir: ptsDir, exercisesTrend: exTrend, exercisesTrendDir: exDir };
  })();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading your progress dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Extra spacing below fixed header (35px total) */}
      <div className="h-[35px]" />
      {/* Page Header */}
      <div className={`bg-card border-b border-border ${isPremium ? '' : 'mt-16 sm:mt-20'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Progress Dashboard</h1>
              <p className="text-muted-foreground">
                Track your career development journey and achievements
              </p>
            </div>
            {/* Centered audio between title (left) and user name (right) on larger screens */}
            <div className={`sm:flex-1 sm:flex sm:justify-center ${isPremium ? '' : 'mt-8'}`}>
              <audio
                src={audioSrc}
                controls
                preload="metadata"
                className="h-8 max-w-xs"
              />
            </div>
            <div className="flex items-center space-x-3">
              {user && (
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-muted rounded-lg">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="User" size={14} color="white" />
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {userProfile?.full_name || (user?.email === 'info@skillbinder.com' ? 'Glenn Donnelly' : user?.email?.split('@')[0])}
                  </span>
                  {isPremium && (
                    <Icon name="Crown" size={14} className="text-warning" />
                  )}
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                onClick={() => window.location?.reload()}
              >
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* AdSense Banner - Below Header - Only for free users */}
      {!isPremium && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1">
          <AdSense 
            adSlot="2014575505" 
            adFormat="auto" 
            className="text-center"
            adStyle={{ display: 'block', minHeight: '30px' }}
          />
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Progress Overview Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Progress Ring */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-lg p-6 text-center">
                <ProgressRing
                  percentage={overallProgress}
                  title="Career Progress"
                  subtitle={currentPlan ? `${currentPlan?.occupation} Development` : "No Active Plan"}
                  size="lg"
                />
                {currentPlan && (
                  <div className="mt-4 text-sm text-muted-foreground">
                    Week {currentPlan?.currentWeek} • {currentPlan?.completedGoals}/{currentPlan?.totalGoals} Goals Complete
                  </div>
                )}
              </div>
            </div>

            {/* Metrics Cards */}
            <div className="lg:col-span-2">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricsCard
                  title="Exercises Done"
                  value={completedExercises}
                  subtitle="Last 7 days"
                  icon="CheckCircle"
                  color="success"
                  trend={exercisesTrend}
                  trendDirection={exercisesTrendDir}
                />
                <MetricsCard
                  title="Active Goals"
                  value={activeGoals}
                  subtitle="In progress"
                  icon="Target"
                  color="primary"
                />
                <MetricsCard
                  title="Current Streak"
                  value={`${currentStreak} days`}
                  subtitle="Keep it up!"
                  icon="Flame"
                  color="warning"
                  trend={8}
                  trendDirection="up"
                />
                <MetricsCard
                  title="Total Points"
                  value={totalPoints}
                  subtitle="All time"
                  icon="Award"
                  color="secondary"
                  trend={pointsTrend}
                  trendDirection={pointsTrendDir}
                />
              </div>
            </div>
          </div>


          {/* Main Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-8">
              {/* Plan Management */}
              <PlanManagement
                currentPlan={currentPlan}
                planHistory={[]} // Mock empty history for demo
                user={user}
                onRegeneratePlan={handleRegeneratePlan}
              />

              {/* Exercise Summary */}
              <ExerciseSummary
                upcomingExercises={upcomingExercises}
                overdueExercises={overdueExercises}
                completionTrend={completionTrend}
                user={user}
                isPremium={isPremium}
              />

              {/* Custom KPI Tracking - Premium Only */}
              {isPremium && (
                <CustomKPITracking
                  kpis={customKPIs}
                  user={user}
                  isPremium={isPremium}
                  onUpdateKPI={handleUpdateKPI}
                />
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-8">
              {/* AdSense Sidebar - Only for free users */}
              {!isPremium && (
                <div className="bg-card border border-border rounded-lg p-4">
                  <AdSense 
                    adSlot="2014575505" 
                    adFormat="auto" 
                    className="text-center"
                    adStyle={{ display: 'block', minHeight: '60px' }}
                  />
                </div>
              )}

              {/* Activity Timeline */}
              <ActivityTimeline activities={activities} />

              {/* Quick Actions */}
              <QuickActions
                user={user}
                userProfile={userProfile}
                isPremium={isPremium}
                currentPlan={currentPlan}
                hasUpcomingExercises={upcomingExercises?.length > 0}
                onGenerateWeeklyCheckin={handleGenerateWeeklyCheckin}
              />

              {/* Subscription Status - hide for premium users */}
              {!isPremium && <SubscriptionStatus user={user} />}
            </div>
          </div>

          {/* Mobile-Optimized Quick Actions */}
          <div className="lg:hidden">
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="font-semibold text-foreground mb-4">Quick Navigation</h3>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Play"
                  iconPosition="left"
                  fullWidth
                  asChild
                >
                  <Link to="/exercise-management">Exercises</Link>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="FileText"
                  iconPosition="left"
                  fullWidth
                  asChild
                >
                  <Link to="/ai-plan-generation">View Plan</Link>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ClipboardList"
                  iconPosition="left"
                  fullWidth
                  asChild
                >
                  <Link to="/career-assessment-setup">New Assessment</Link>
                </Button>
                {!isPremium && (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Crown"
                    iconPosition="left"
                    fullWidth
                    asChild
                  >
                    <Link to="/subscription-management">Upgrade</Link>
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* AdSense Footer Banner - Only for free users */}
          {!isPremium && (
            <div className="text-center py-1">
              <AdSense 
                adSlot="2014575505" 
                adFormat="auto" 
                className="text-center"
                adStyle={{ display: 'block', minHeight: '30px' }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ProgressDashboard;